import { createContext, createElement, useContext, type ReactNode } from "react";
import type { ApolloClient } from "@apollo/client";

const RewardsApolloClientContext = createContext<ApolloClient | undefined>(undefined);

export interface IRewardsApolloProviderProps {
  /** The Apollo client every rewards hook below this provider should use. */
  client: ApolloClient;
  children?: ReactNode;
}

/**
 * Routes every rewards hook rendered below it to `client` instead of the
 * nearest `<ApolloProvider>` client.
 *
 * Customer/promoter operations live on the app's main `/graphql` endpoint, so
 * the customer app needs nothing. Admin operations live on the separate
 * `/platform/admin/rewards/graphql` endpoint: the admin app creates a dedicated
 * client for it and wraps the rewards screens in this provider, leaving the
 * app's own `<ApolloProvider>` untouched.
 *
 * @example
 * <RewardsApolloProvider client={rewardsAdminClient}>
 *   <RewardsAdminApp ... />
 * </RewardsApolloProvider>
 */
export function RewardsApolloProvider({ client, children }: IRewardsApolloProviderProps) {
  return createElement(RewardsApolloClientContext.Provider, { value: client }, children);
}

/**
 * The client injected by {@link RewardsApolloProvider}, or `undefined` so Apollo
 * falls back to the nearest `<ApolloProvider>`.
 */
export function useRewardsApolloClient(): ApolloClient | undefined {
  return useContext(RewardsApolloClientContext);
}
